import qs from 'qs'
import { useDispatch, useSelector } from 'react-redux'
import { unwrapResult } from '@reduxjs/toolkit'
import {
  checkoutProcessStatusStarted,
  createOrder,
  getOrderWithProducts,
  selectShippingType,
  setShippingType,
  setTotal,
  startCheckout,
  vtexAddToCart,
} from '../reducers/orderSlice'
import UtmUtils from '../utils/utmUtils'
import { openWindow } from '../utils/browserDetectUtils'
import { useCart, usePDP, useProducts } from 'ui'
import { OwnCheckoutIntegrations } from '../constants/integrations'
import StoreUtils from '../utils/storeUtils'
import { useViews } from 'state'
import {
  CALL_CARD_CHECKOUT_FAILURE,
  setActiveCallCard,
  setRedirectDialog,
  setStockError,
} from '../reducers/uiSlice'
import { CheckoutStates } from '../constants/checkoutStates'
import PaymentGateways from '../strategy/payment/constants'
import { usePIP } from './usePIP'
import { useInHouseMetrics, usePostHogAnalytics } from '@gojiraf/analytics'
import { useEffect, useState } from 'react'
import { ShippingTypes } from '../constants/shippingTypes'
import { useUtm } from '@gojiraf/useutm'

export const useCheckout = (store) => {
  const [redirection, setRedirection] = useState(false)
  const [clickBuyCheckout, setClickBuyCheckout] = useState(false)
  const [variantUrl, setVariantUrl] = useState(null)
  const [_orderData, _setOrderData] = useState(null)
  const [paymentGateway] = StoreUtils.getPaymentGateways(store)
  const dispatch = useDispatch()
  const shippingType = useSelector(selectShippingType)
  const queryParams = qs.parse(window.location.search, { ignoreQueryPrefix: true })
  const { utm_medium } = UtmUtils.getUtmObject(queryParams)
  const { orderFormId, shippingAddress } = queryParams
  const { isAppView } = useUtm(utm_medium)
  const { getProductById, products: productsList } = useProducts()
  const { trackEvent: inHouseTrackEvent } = useInHouseMetrics()
  const { postHogTrackEvent } = usePostHogAnalytics()
  const { setEnabled, isEnabled, canPIP } = usePIP()
  const hasOwnCheckout = OwnCheckoutIntegrations.includes(paymentGateway.type)
  const isToAgreeShippingType = paymentGateway.type === PaymentGateways.TO_AGREE
  const isLightIntegration = paymentGateway.type === PaymentGateways.LIGHT
  const isVtexExternalCart =
    paymentGateway.type === PaymentGateways.VTEX && !!orderFormId && !!shippingAddress
  const { send: sendViews } = useViews()
  const { state: pdpState, send: sendPDP } = usePDP()
  const { send: sendCart, state: cartState } = useCart()

  const clearCart = () => {
    sendCart({ type: 'Reset Cart' })
  }

  const goToPDP = (productId) => {
    sendPDP({
      type: 'SET_PRODUCT',
      productId,
    })
    sendViews({
      type: 'SHOW_PDP',
    })
  }

  const goBack = () => {
    sendViews({ type: 'GO_BACK' })
  }

  const setCheckoutStatus = (checkoutStatus, checkoutWindow) => {
    if (checkoutStatus === CheckoutStates.INSUFFICIENT_STOCK) {
      checkoutWindow.close()
      sendViews({ type: 'SHOW_ORDER_STATUS' })
      dispatch(setStockError(true))
      dispatch(setActiveCallCard(CALL_CARD_CHECKOUT_FAILURE))
      return
    }
    if (checkoutStatus === CheckoutStates.NOT_INITIALIZED) {
      checkoutWindow.close()
      goBack()
      return
    }
    goBack()
  }

  const getQuantityDiff = (selectedVariant, selectedQuantity) => {
    const currentQuantityInCart = cartState.context.variants.get(selectedVariant.id) ?? 0
    const { limitPerOrder } = selectedVariant
    const exceedsLimit = currentQuantityInCart + selectedQuantity > limitPerOrder
    let quantityDiff = 0

    if (limitPerOrder === undefined) return { exceedsLimit, quantityDiff }
    if (exceedsLimit) {
      quantityDiff = currentQuantityInCart + selectedQuantity - limitPerOrder
    }

    return { quantityDiff, exceedsLimit }
  }

  const handleAddProductToCart = (selectedVariant, selectedQuantity) => {
    const { exceedsLimit, quantityDiff } = getQuantityDiff(selectedVariant, selectedQuantity)
    if (exceedsLimit === true && quantityDiff === 0) {
      goBack()
      return
    }

    if (cartState.context.variants.size === 0) {
      postHogTrackEvent('cart-initialized')
    }
    postHogTrackEvent('product-add-to-cart', {
      productName: selectedVariant?.name,
      from: 'product',
    })

    if (isVtexExternalCart) {
      dispatch(
        vtexAddToCart({
          paymentGateway,
          product: { sku: selectedVariant.sku, quantity: selectedQuantity - quantityDiff },
          orderFormId,
        }),
      )
      return
    }

    sendCart({
      type: 'Add product variant',
      productVariantId: selectedVariant.id,
      quantity: selectedQuantity - quantityDiff,
    })
  }

  const getDefaultDeliveryMethod = () => {
    if (hasOwnCheckout) return ShippingTypes.UNAVAILABLE
    if (isToAgreeShippingType) return ShippingTypes.TO_AGREE
    return shippingType
  }

  const handleOnBuyClicked = async ({ total, selectedDeliveryMethod }) => {
    dispatch(setShippingType(selectedDeliveryMethod))
    dispatch(setTotal({ total: total }))
    if (hasOwnCheckout) await handleOwnCheckout()
    if (getDefaultDeliveryMethod() !== 'unavailable') sendViews({ type: 'GO_FORWARD' })
  }

  const handleQuickAddClick = async (featuredProduct) => {
    postHogTrackEvent('checkout-from-quick-buy', {
      productName: featuredProduct.name,
      from: 'quick-buy',
    })
    if (isLightIntegration) {
      postHogTrackEvent('cart-initialized')
      handlePIPLightIntegration(featuredProduct, featuredProduct.skus[0].externalUrl)
      return
    }
    if (featuredProduct.skus?.length > 1) {
      goToPDP(featuredProduct.id)
      return
    }
    if (hasOwnCheckout || isVtexExternalCart) {
      handleAddProductToCart(featuredProduct.skus[0], 1)
      if (!isVtexExternalCart) setClickBuyCheckout(true)
      return
    }
    goToPDP(featuredProduct.id)
  }

  const getItemsInCart = () => {
    const cartItems = []

    cartState.context.variants.forEach((variantQuantity, variantId) => {
      let variantFound

      const productFound = productsList?.find((findProduct) => {
        variantFound = findProduct.skus.find((variant) => variant.id === variantId)

        if (variantFound !== undefined) {
          return getProductById(pdpState.context.productId)
        }

        return undefined
      })

      if (productFound !== undefined && variantFound !== undefined) {
        cartItems.push({
          quantity: variantQuantity,
          product: productFound,
          ...variantFound,
        })
      }
    })

    return cartItems
  }

  const startCheckoutOwnWindowIntegrations = async ({ orderData, checkoutWindow }) => {
    try {
      await dispatch(createOrder(orderData)).then(unwrapResult)
      await dispatch(checkoutProcessStatusStarted()).then(unwrapResult)
      await dispatch(getOrderWithProducts()).then(unwrapResult)
      const startCheckoutStatus = await dispatch(
        startCheckout({ store, checkoutWindow, paymentGateway, productsList }),
      ).then(unwrapResult)
      setCheckoutStatus(startCheckoutStatus, checkoutWindow)
      clearCart()
    } catch (error) {
      console.error(error)
      goBack()
    }
  }

  const goToCheckout = async ({ orderData, checkoutWindow, selectedPaymentGateway, onGoBack }) => {
    const isPaymentGatewayPayPal = selectedPaymentGateway.type === PaymentGateways.PAYPAL
    try {
      await dispatch(createOrder(orderData)).then(unwrapResult)
      await dispatch(checkoutProcessStatusStarted()).then(unwrapResult)
      await dispatch(getOrderWithProducts()).then(unwrapResult)
      const startCheckoutStatus = await dispatch(
        startCheckout({
          productsList,
          store,
          checkoutWindow,
          paymentGateway: selectedPaymentGateway,
          orderData,
        }),
      ).then(unwrapResult)
      if (!isPaymentGatewayPayPal && startCheckoutStatus === CheckoutStates.STARTED) {
        clearCart()
      } else if (startCheckoutStatus !== CheckoutStates.STARTED) {
        checkoutWindow.close()
        return startCheckoutStatus
      }
    } catch (err) {
      console.error(err)
      onGoBack()
    }
  }

  const handleOwnCheckout = async () => {
    const details = []
    for (const [skuId, quantity] of cartState.context.variants.entries()) {
      details.push({
        skuId,
        quantity,
      })
    }

    const orderData = {
      paymentType: paymentGateway.type,
      utmParams: { ...UtmUtils.getUtmObject(queryParams) },
      details,
    }
    if (!isEnabled && canPIP) {
      postHogTrackEvent('pip-open-auto')
      _setOrderData(orderData)
      setRedirection(true)
      setEnabled(true)
      return
    } else {
      postHogTrackEvent('checkout-started')
      inHouseTrackEvent('checkout-started')
      const checkoutWindow = openWindow(isAppView, paymentGateway.useTarget)
      startCheckoutOwnWindowIntegrations({ orderData, checkoutWindow })
    }
  }

  const handlePIPLightIntegration = (product, selectedVariantURL) => {
    postHogTrackEvent('click-view-more-integration-light', {
      productName: product.name,
      from: 'product',
    })
    const url = new URL(selectedVariantURL)
    url.searchParams.set('utm_source', 'gojiraf')
    url.searchParams.set('utm_medium', 'participant')
    url.searchParams.set('utm_campaign', 'socios')

    if (!isEnabled && canPIP) {
      postHogTrackEvent('pip-open-auto')
      setVariantUrl(url.toString())
      setRedirection(true)
      setEnabled(true)
    } else {
      postHogTrackEvent('checkout-started')
      inHouseTrackEvent('checkout-started')
      const anchor = document.createElement('a')
      anchor.href = url.toString()
      anchor.target = '_blank'
      anchor.click()
      goBack()
    }
  }

  useEffect(() => {
    if (redirection) {
      setRedirection(false)
      if (isEnabled) {
        if (isLightIntegration || hasOwnCheckout) {
          dispatch(
            setRedirectDialog({
              paymentGateway,
              orderData: hasOwnCheckout ? _orderData : null,
              externalUrl: hasOwnCheckout ? null : variantUrl,
              showDialog: true,
            }),
          )
        }
      }
    }
  }, [isEnabled])

  return {
    hasOwnCheckout,
    isToAgreeShippingType,
    isLightIntegration,
    isVtexExternalCart,
    handlePIPLightIntegration,
    clearCart,
    goToCheckout,
    getItemsInCart,
    handleAddProductToCart,
    handleOnBuyClicked,
    handleQuickAddClick,
    clickBuyCheckout,
    setClickBuyCheckout,
    defaultDeliveryMethod: getDefaultDeliveryMethod(),
  }
}
